import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardBody, CardFooter, CardHeader, CardTitle } from "@hp/ui";
import type { AlertSettings, GenieAnswerShape, GenieMeaningSearch, GenieOnOff, GenieResearchMode } from "@hp/api-client";
import { updateAlertSettings } from "../api/alertSettings";
import { ErrorBlock } from "./QueryStatus";

interface Choice<T extends string> {
  value: T;
  label: string;
}

const FOLLOW: Choice<"env"> = { value: "env", label: "Follow the server's setting" };

const SHAPES: Choice<GenieAnswerShape>[] = [
  FOLLOW,
  { value: "auto", label: "Automatic: short for a figure or a clarification, full for a chain of documents" },
  { value: "classic", label: "Always the full explanation (contract, orders, rules, disputes, agreement)" },
  { value: "short", label: "Always short (only the layers that carry a finding)" },
  { value: "structured", label: "Structured: bold answer and source first, tables, worked arithmetic, inline references" },
];
const CACHE: Choice<GenieOnOff>[] = [
  FOLLOW,
  { value: "on", label: "On: serve a repeated question from the saved answer" },
  { value: "off", label: "Off: write every answer again" },
];
const RESEARCH: Choice<GenieResearchMode>[] = [
  FOLLOW,
  { value: "deep", label: "Only when a person switches Deep research on in the chat" },
  { value: "on", label: "Every question (slower: about a minute and a half each)" },
  { value: "off", label: "Never" },
];
const MEANING: Choice<GenieMeaningSearch>[] = [
  FOLLOW,
  { value: "on", label: "On: find passages that say the same thing in other words" },
  { value: "off", label: "Off: keyword search only" },
  { value: "hashing", label: "Stand-in (for tests, finds little)" },
];

function Switch<T extends string>({
  label,
  hint,
  value,
  choices,
  onChange,
}: {
  label: string;
  hint: string;
  value: T;
  choices: Choice<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <label className="block text-sm text-zinc-800">
      <span className="font-medium">{label}</span>
      <select
        className="mt-1 block w-full max-w-xl rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        aria-label={label}
      >
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-zinc-500">{hint}</span>
    </label>
  );
}

/** HP Genie's switches (what the server's HP_ANSWER_SHAPE, HP_ANSWER_CACHE, HP_LOOKUP_AGENTIC and HP_VECTOR_SEARCH settings do). Admin only. */
export function GenieSwitchesForm({ settings }: { settings: AlertSettings }) {
  const queryClient = useQueryClient();
  const saved = {
    answer_shape: settings.answer_shape ?? "env",
    answer_cache: settings.answer_cache ?? "env",
    research_mode: settings.research_mode ?? "env",
    meaning_search: settings.meaning_search ?? "env",
  };
  const [shape, setShape] = React.useState<GenieAnswerShape>(saved.answer_shape);
  const [cache, setCache] = React.useState<GenieOnOff>(saved.answer_cache);
  const [research, setResearch] = React.useState<GenieResearchMode>(saved.research_mode);
  const [meaning, setMeaning] = React.useState<GenieMeaningSearch>(saved.meaning_search);
  React.useEffect(() => {
    setShape(saved.answer_shape);
    setCache(saved.answer_cache);
    setResearch(saved.research_mode);
    setMeaning(saved.meaning_search);
  }, [saved.answer_shape, saved.answer_cache, saved.research_mode, saved.meaning_search]);

  const mutation = useMutation({
    mutationFn: updateAlertSettings,
    onSuccess: (updated) => queryClient.setQueryData(["alert-settings"], updated),
  });
  const changed = shape !== saved.answer_shape || cache !== saved.answer_cache || research !== saved.research_mode || meaning !== saved.meaning_search;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate({ answer_shape: shape, answer_cache: cache, research_mode: research, meaning_search: meaning });
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle>HP Genie switches</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <Switch
            label="Answer shape"
            hint="How long and how structured an answer is. Applies to every question from the next one."
            value={shape}
            choices={SHAPES}
            onChange={setShape}
          />
          <Switch
            label="Answer cache"
            hint="A question asked again, in the same chat, is answered at once from the database while nothing it rests on has changed: a new or archived document, a changed disputes list or a new admin note on feedback makes it write the answer again."
            value={cache}
            choices={CACHE}
            onChange={setCache}
          />
          <Switch
            label="Deep research"
            hint="Plans the question, searches each part and checks for gaps before answering. Slower and more complete. The switch in the chat only applies when this is not set to Every question or Never."
            value={research}
            choices={RESEARCH}
            onChange={setResearch}
          />
          <Switch
            label="Meaning search"
            hint="Finds passages that say the same thing in other words, beside the keyword search. Switch it off only to compare."
            value={meaning}
            choices={MEANING}
            onChange={setMeaning}
          />
        </CardBody>
        <CardFooter className="flex flex-wrap items-center justify-between gap-3">
          <Button type="submit" disabled={mutation.isPending || !changed}>
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
          <span className="text-xs text-zinc-500">
            {mutation.isSuccess ? "Saved. " : ""}Applies to everyone on the team, from the next question. Only an admin can change it.
          </span>
        </CardFooter>
        {mutation.isError ? <ErrorBlock message="Could not save. Only an admin can change this." surface="canvas" /> : null}
      </Card>
    </form>
  );
}
